/**
 * Brands Step Modal
 *
 * Fourth step of onboarding - collects favorite brands
 * Figma: Property 1=Sign up 4
 * Step 4 of 4
 */
"use client";

import { brandSearchErrorMessage } from "@/lib/brand-search-state";

import React, { useEffect, useMemo, useRef, useState } from "react";

import { motion } from "framer-motion";
import { Loader2, Search, X } from "lucide-react";

import { getFeaturedBrandsByGender } from "@/constants/featured-brands";
import {
  listBrandNames,
  searchBrandNamesByPrefix,
  searchBrandsIndexByBrands,
} from "@/lib/shop-agent-client";
import { colors, modalSpacing } from "@/styles/design-tokens";

import { BasicModalShell } from "./BasicModalShell";
import { ModalPrimaryButton } from "./ModalPrimaryButton";
import { ModalProgressBar } from "./ModalProgressBar";
import { PreviousSection } from "./PreviousSection";

const SPACING = {
  contentGap: "24px", // Unique spacing for brands modal
  formGap: "20px", // Unique spacing for brands modal
  topPadding: "76px", // Unique to this modal
} as const;

type ShoppingPreference = "WOMEN" | "MEN" | "BOTH";

interface BrandsStepModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNext: (brands: string[]) => void;
  onBack: () => void;
  initialBrands?: string[];
  shoppingPreference?: ShoppingPreference | null;
  currentStep?: number;
  totalSteps?: number;
}

export function BrandsStepModal({
  isOpen,
  onClose,
  onNext,
  onBack,
  initialBrands = [],
  shoppingPreference = null,
  currentStep = 4,
  totalSteps = 4,
}: BrandsStepModalProps) {
  const [favoriteBrands, setFavoriteBrands] = useState<string[]>(initialBrands);
  const [serverResults, setServerResults] = useState<string[]>([]);
  const [allBrands, setAllBrands] = useState<string[]>([]);
  const [isFocused, setIsFocused] = useState(false);
  const [featuredBrands, setFeaturedBrands] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [searchError, setSearchError] = useState<string | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [loadingIndex, setLoadingIndex] = useState<number | null>(null);
  const [replacementStatus, setReplacementStatus] = useState<{
    message: string;
    isError: boolean;
  } | null>(null);
  const replacementRequestRef = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const genderKey =
    shoppingPreference === "MEN" ? "mens" : shoppingPreference === "WOMEN" ? "womens" : "combined";

  // Load featured brands based on gender preference
  useEffect(() => {
    const featured = getFeaturedBrandsByGender(genderKey);
    setFeaturedBrands(featured);
  }, [genderKey]);

  useEffect(() => {
    setLoadingIndex(null);
    setReplacementStatus(null);
    return () => {
      // A hidden/unmounted modal or a new preference no longer owns this request.
      replacementRequestRef.current += 1;
    };
  }, [isOpen, genderKey]);

  // Debounced typeahead for brand search
  useEffect(() => {
    let cancelled = false;
    const q = inputValue.trim();
    setServerResults([]);
    setSearchError(null);
    const handle = setTimeout(() => {
      (async () => {
        if (!q) return;
        try {
          const results = await searchBrandNamesByPrefix(q, 25);
          if (!cancelled) setServerResults(results);
        } catch (error) {
          if (!cancelled) {
            setServerResults([]);
            setSearchError(brandSearchErrorMessage(error));
          }
        }
      })();
    }, 150);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [inputValue]);

  const searchResults = useMemo(() => {
    const base = inputValue.trim() ? serverResults : allBrands;
    const safeBase = Array.isArray(base) ? base : [];
    return safeBase.filter((b) => !favoriteBrands.includes(b));
  }, [serverResults, allBrands, inputValue, favoriteBrands]);

  useEffect(() => {
    const hasQuery = inputValue.trim().length > 0;
    const shouldShow = (hasQuery || isFocused) && searchResults.length > 0;
    setShowDropdown(shouldShow);
    setHighlightedIndex(-1);
  }, [inputValue, isFocused, searchResults.length]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const addBrand = (brandName: string) => {
    const clean = brandName.trim();
    if (clean && !favoriteBrands.includes(clean)) {
      setFavoriteBrands([...favoriteBrands, clean]);
      setInputValue("");
      setShowDropdown(false);
      setHighlightedIndex(-1);
    }
  };

  const removeBrand = (brand: string) => {
    setFavoriteBrands(favoriteBrands.filter((b) => b !== brand));
  };

  const handleBrandToggle = (brand: string) => {
    if (favoriteBrands.includes(brand)) {
      removeBrand(brand);
    } else {
      setFavoriteBrands([...favoriteBrands, brand]);
    }
  };

  const replaceWithSimilarBrand = async (brand: string, index: number) => {
    const requestId = ++replacementRequestRef.current;
    setReplacementStatus(null);
    try {
      setLoadingIndex(index);
      const response = await searchBrandsIndexByBrands([brand], 5);
      if (requestId !== replacementRequestRef.current) return;
      const candidates = Array.isArray(response?.results) ? response.results : [];
      const currentSet = new Set(featuredBrands.map((b) => b.toLowerCase()));
      const replacement = candidates.find(
        (r) =>
          r.brand_name &&
          r.brand_name.toLowerCase() !== brand.toLowerCase() &&
          !currentSet.has(r.brand_name.toLowerCase())
      );
      if (replacement && replacement.brand_name) {
        const brandName = replacement.brand_name;
        setFeaturedBrands((prev) => {
          const next = [...prev];
          next[index] = brandName;
          return next;
        });
      } else {
        setReplacementStatus({
          message: `No more similar brands found for ${brand}.`,
          isError: false,
        });
      }
    } catch (error) {
      if (requestId === replacementRequestRef.current) {
        setReplacementStatus({
          message: `${brand}: ${brandSearchErrorMessage(error)}`,
          isError: true,
        });
      }
    } finally {
      if (requestId === replacementRequestRef.current) setLoadingIndex(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev < searchResults.length - 1 ? prev + 1 : prev));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && searchResults[highlightedIndex]) {
        addBrand(searchResults[highlightedIndex]);
      } else if (inputValue.trim() && searchResults.length > 0) {
        addBrand(searchResults[0]);
      }
    } else if (e.key === "Escape") {
      setShowDropdown(false);
      setHighlightedIndex(-1);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (favoriteBrands.length > 0) {
      onNext(favoriteBrands);
    }
  };

  return (
    <BasicModalShell
      isOpen={isOpen}
      onClose={onClose}
      closeOnBackdropClick={false}
      panelClassName="sm:min-h-[650px]"
      scrollClassName="overflow-hidden sm:overflow-y-auto"
      topSlot={
        <ModalProgressBar
          currentStep={currentStep}
          totalSteps={totalSteps}
          animateFromPrevious={true}
        />
      }
    >
      <div
        className="mx-auto flex flex-1 flex-col px-4 sm:px-0"
        style={{
          width: "340px",
          maxWidth: "100%",
          gap: SPACING.contentGap,
          paddingTop: SPACING.topPadding,
          paddingBottom: modalSpacing.contentBottomPadding,
        }}
      >
        {/* Header with Title and Subtitle */}
        <div
          className="flex flex-col items-center text-center"
          style={{ gap: modalSpacing.headerGap }}
        >
          <h2 className="text-title text-ink">What are your favorite brands?</h2>
          <p className="text-caption text-ink-secondary">
            Select brands you love to help us make better recommendations.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col" style={{ gap: SPACING.formGap }}>
          {/* Selected Brands */}
          {favoriteBrands.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="flex flex-wrap gap-2"
            >
              {favoriteBrands.map((brand) => (
                <motion.div
                  key={brand}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="inline-flex items-center gap-2 rounded-md bg-black px-3 py-1.5 text-sm text-white"
                >
                  {brand}
                  <button
                    type="button"
                    onClick={() => removeBrand(brand)}
                    className="transition-opacity hover:opacity-70"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* Brand Search Input */}
          <div className="relative">
            <div
              className="relative flex items-center rounded border border-gray-200 px-3"
              style={{ height: modalSpacing.inputFieldHeight }}
            >
              <Search
                className="h-4 w-4 flex-shrink-0"
                style={{ color: colors.text.accentMdGray }}
              />
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={async () => {
                  setIsFocused(true);
                  if (allBrands.length === 0) {
                    try {
                      const names = await listBrandNames();
                      setAllBrands(names);
                    } catch {
                      setAllBrands([]);
                    }
                  }
                  if (searchResults.length > 0) setShowDropdown(true);
                }}
                onBlur={() => setIsFocused(false)}
                className="text-input-modal text-ink h-full w-full bg-transparent pl-3 outline-none"
                placeholder="Search brands..."
                aria-label="Search brands"
              />
            </div>

            {searchError && (
              <p role="alert" className="mt-2 text-caption text-ink-secondary">
                {searchError}
              </p>
            )}
            {/* Dropdown */}
            {showDropdown && searchResults.length > 0 && (
              <div
                ref={dropdownRef}
                className="absolute left-0 right-0 top-full z-50 mt-1 max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg"
              >
                {searchResults.slice(0, 10).map((brand, index) => (
                  <button
                    key={brand}
                    type="button"
                    onClick={() => addBrand(brand)}
                    className={`w-full px-4 py-2 text-left text-sm transition-colors ${
                      index === highlightedIndex ? "bg-gray-100" : "hover:bg-gray-50"
                    }`}
                  >
                    {brand}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Popular Brands Grid */}
          {featuredBrands.length > 0 && (
            <div className="space-y-3">
              <label
                style={{
                  ...{
                    fontFamily: "Inter, sans-serif",
                    fontSize: "12px",
                    fontWeight: 500,
                  },
                  color: colors.text.black,
                }}
              >
                Popular brands
              </label>
              <div
                className="grid max-h-40 grid-cols-2 gap-2 overflow-y-auto overscroll-contain touch-pan-y"
                style={{ WebkitOverflowScrolling: "touch" }}
              >
                {featuredBrands.slice(0, 24).map((brand, index) => (
                  <motion.button
                    key={brand}
                    type="button"
                    onClick={() => {
                      handleBrandToggle(brand);
                      replaceWithSimilarBrand(brand, index);
                    }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.01 }}
                    className={`rounded text-xs transition-all duration-200 ${
                      favoriteBrands.includes(brand)
                        ? "bg-black text-white"
                        : "border border-gray-200 bg-white hover:border-gray-300"
                    }`}
                    style={{ height: modalSpacing.pillButtonHeight }}
                  >
                    {loadingIndex === index ? (
                      <span className="inline-flex items-center justify-center gap-2">
                        <Loader2 className="h-3 w-3 animate-spin" />
                      </span>
                    ) : (
                      brand
                    )}
                  </motion.button>
                ))}
              </div>
              {replacementStatus && (
                <p
                  role={replacementStatus.isError ? "alert" : "status"}
                  className="text-caption text-ink-secondary"
                >
                  {replacementStatus.message}
                </p>
              )}
            </div>
          )}

          {/* Complete Button */}
          <ModalPrimaryButton type="submit" disabled={favoriteBrands.length === 0}>
            Complete
          </ModalPrimaryButton>
        </form>

        {/* Previous Section */}
        <PreviousSection onBack={onBack} paddingTop="50px" paddingBottom="16px" />
      </div>
    </BasicModalShell>
  );
}
